import { useState } from "react";
import { useCustom, useCustomMutation, usePermissions } from "@refinedev/core";
import { FileText, History, NotebookPen, Search } from "lucide-react";

import type { PensionKlantNotitie } from "../pensionKlantTypes";
import { isAutomatisch } from "./boekingen";
import type { Context } from "./hulp";
import { Rondje } from "./Tegels";
import { Laadstaat, Leeg, Sectie, Verder } from "./Sectie";

/** Een notitie langer dan dit staat eerst ingeklapt. */
const LANG = 360;
const EERST_ZICHTBAAR = 20;

type Soort = "alles" | "notities" | "logboek";

export function useKlantNotities(klantId: string) {
  return useCustom<PensionKlantNotitie[]>({ url: `pension/klanten/${klantId}/notities`, method: "get" });
}

/** "8 jul 2025, 11:10". */
export function notitieDatum(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
    .format(d)
    .replace(".", "");
}

/**
 * De notities bij de klant. Links de lijst, nieuwste eerst, met zoeken en een filter dat het logboek
 * dat het oude pakket in dezelfde tabel bijhield apart zet. Rechts een nieuwe notitie
 * toevoegen, voor wie de klant mag bewerken.
 */
export function NotitiesTab({ klantId, ctx }: { klantId: string; ctx: Context }) {
  const { t } = ctx;
  const { data, isLoading, isError, refetch } = useKlantNotities(klantId);
  const { data: rechten } = usePermissions<string[]>();
  const magSchrijven = Array.isArray(rechten) && rechten.includes("ROLE_RELATIE_EDIT");
  const [zoek, setZoek] = useState("");
  const [soort, setSoort] = useState<Soort>("alles");
  const [alles, setAlles] = useState(false);

  const notities = data?.data ?? [];
  const q = zoek.trim().toLowerCase();
  const gefilterd = notities.filter((n) => {
    if (soort === "notities" && isAutomatisch(n)) return false;
    if (soort === "logboek" && !isAutomatisch(n)) return false;
    if (q === "") return true;
    return [n.notitie, n.opmerking, n.melder].some((x) => x?.toLowerCase().includes(q));
  });
  const zichtbaar = alles ? gefilterd : gefilterd.slice(0, EERST_ZICHTBAAR);

  return (
    <div className="pk-blad pk-blad--notities">
      <Sectie titel={t("pensionKlant.tab.notities")} icoon={FileText} tint="paars" aantal={notities.length}>
        <div className="pk-notities__balk">
          <label className="pk-zoek">
            <Search size={15} aria-hidden="true" />
            <input
              type="search"
              autoComplete="off"
              value={zoek}
              placeholder={t("pensionKlant.notities.zoek")}
              aria-label={t("pensionKlant.notities.zoek")}
              onChange={(e) => setZoek(e.target.value)}
            />
          </label>
          <div className="pk-filter" role="radiogroup" aria-label={t("pensionKlant.notities.soortAria")}>
            {(["alles", "notities", "logboek"] as Soort[]).map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={soort === s}
                className="pk-filter__knop"
                onClick={() => setSoort(s)}
              >
                {t(`pensionKlant.notities.soort.${s}`)}
              </button>
            ))}
          </div>
        </div>
        <Laadstaat laden={isLoading} fout={isError} onOpnieuw={() => refetch()} ctx={ctx} />
        {!isLoading && !isError && gefilterd.length === 0 && (
          <Leeg>{notities.length === 0 ? t("pensionKlant.notities.geen") : t("pensionKlant.notities.geenGevonden")}</Leeg>
        )}
        <ol className="pk-notities">
          {zichtbaar.map((n) => (
            <Notitie key={n.id} notitie={n} ctx={ctx} />
          ))}
        </ol>
        {gefilterd.length > EERST_ZICHTBAAR && (
          <Verder onClick={() => setAlles((x) => !x)}>
            {alles ? t("pensionKlant.blad.minder") : t("pensionKlant.blad.alle", { args: [gefilterd.length] })}
          </Verder>
        )}
      </Sectie>
      {magSchrijven && <NieuweNotitie klantId={klantId} ctx={ctx} onToegevoegd={() => refetch()} />}
    </div>
  );
}

function Notitie({ notitie: n, ctx }: { notitie: PensionKlantNotitie; ctx: Context }) {
  const { t, locale } = ctx;
  const [open, setOpen] = useState(false);
  const automatisch = isAutomatisch(n);
  const lang = n.notitie.length > LANG;
  return (
    <li className="pk-notitie" data-automatisch={automatisch || undefined}>
      <Rondje icoon={automatisch ? History : FileText} tint={automatisch ? "grijs" : "paars"} />
      <div className="pk-notitie__inhoud">
        <p className="pk-notitie__kop">
          <strong>{n.melder}</strong>
          <time dateTime={n.datum}>{notitieDatum(n.datum, locale)}</time>
          {automatisch && <span className="pk-chip">{t("pensionKlant.notities.automatisch")}</span>}
        </p>
        <p className="pk-notitie__tekst" data-ingeklapt={lang && !open ? "" : undefined}>
          {n.notitie}
        </p>
        {lang && (
          <button type="button" className="pk-notitie__meer" aria-expanded={open} onClick={() => setOpen((x) => !x)}>
            {open ? t("pensionKlant.blad.minder") : t("pensionKlant.blad.meer")}
          </button>
        )}
        {n.opmerking && <p className="pk-notitie__opmerking">{n.opmerking}</p>}
      </div>
    </li>
  );
}

function NieuweNotitie({ klantId, ctx, onToegevoegd }: { klantId: string; ctx: Context; onToegevoegd: () => void }) {
  const { t } = ctx;
  const [tekst, setTekst] = useState("");
  const [opmerking, setOpmerking] = useState("");
  const [melding, setMelding] = useState<{ soort: "ok" | "fout"; tekst: string } | null>(null);
  const { mutateAsync, isLoading: bezig } = useCustomMutation<PensionKlantNotitie>();

  const opslaan = async () => {
    if (tekst.trim() === "") return;
    setMelding(null);
    try {
      await mutateAsync({
        url: `pension/klanten/${klantId}/notities`,
        method: "post",
        values: { notitie: tekst, opmerking: opmerking.trim() === "" ? null : opmerking },
        successNotification: false,
        errorNotification: false,
      });
      setTekst("");
      setOpmerking("");
      setMelding({ soort: "ok", tekst: t("pensionKlant.notities.opgeslagen") });
      onToegevoegd();
    } catch {
      setMelding({ soort: "fout", tekst: t("pensionKlant.notities.opslaanMislukt") });
    }
  };

  return (
    <Sectie titel={t("pensionKlant.notities.nieuw")} icoon={NotebookPen} tint="groen">
      <form
        className="pk-notitieformulier"
        onSubmit={(e) => {
          e.preventDefault();
          void opslaan();
        }}
      >
        <label className="pk-veld">
          <span>{t("pensionKlant.notities.tekst")}</span>
          <textarea value={tekst} rows={6} maxLength={8000} onChange={(e) => setTekst(e.target.value)} required />
        </label>
        <label className="pk-veld">
          <span>{t("pensionKlant.notities.opmerking")}</span>
          <input type="text" value={opmerking} onChange={(e) => setOpmerking(e.target.value)} />
        </label>
        <div className="pk-notitieformulier__voet">
          <p className="pk-voetnoot" role="status" data-soort={melding?.soort}>
            {melding?.tekst ?? ""}
          </p>
          <button type="submit" className="pk-knop pk-knop--hoofd" disabled={bezig || tekst.trim() === ""}>
            {t("pensionKlant.notities.opslaan")}
          </button>
        </div>
      </form>
    </Sectie>
  );
}
